import { toTitleCase } from 'src/components/UserRegistrationSteps/StepThree/signupHook';
import {
  EMPTY_FIELD_FORM,
  FieldEditorSubmit,
  FieldFormValues,
  SourceFieldOption
} from '../../helpers/articleTypeConfig.types';
import {
  formatKeyPath,
  toFieldEditorSubmit,
  toFieldFormValues
} from '../../helpers/articleTypeConfig.utils';

export const trimLeadingWhitespace = (value: string) =>
  value.replace(/^\s+/, '');

export const normalizeFieldName = (value: string) => value.trim().toLowerCase();
export const EXTRACTION_SKIP_CHECKBOX_FIELD_NAME = normalizeFieldName(
  'ExtractionSkipCheckbox'
);

export const isDigitsOnly = (value: string) => /^[0-9]+$/.test(value);

export const toNumberIfFinite = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const validateWhitespace = ({
  value,
  label,
  isRequired = false
}: {
  value?: string;
  label: string;
  isRequired?: boolean;
}) => {
  const rawValue = value ?? '';
  const trimmedValue = rawValue.trim();

  if (isRequired) {
    return Boolean(trimmedValue) || `${label} is required`;
  }

  return !rawValue || Boolean(trimmedValue) || `${label} cannot be blank`;
};

export const hasAlphabetCharacter = (value: string) => /[A-Za-z]/.test(value);

export const validateAlphabetRule = ({
  value,
  label,
  isRequired = false
}: {
  value?: string;
  label: string;
  isRequired?: boolean;
}) => {
  const whitespaceResult = validateWhitespace({
    value,
    label,
    isRequired
  });

  if (whitespaceResult !== true) return whitespaceResult;

  const trimmedValue = (value ?? '').trim();
  if (!trimmedValue) return true;

  if (!hasAlphabetCharacter(trimmedValue)) {
    return `${label} cannot contain only numbers or special characters`;
  }

  return true;
};

export const validateOptionalTrimmedText = ({
  value,
  label,
  isDisabled = false
}: {
  value?: string;
  label: string;
  isDisabled?: boolean;
}) => {
  if (isDisabled) return true;

  return validateAlphabetRule({
    value,
    label
  });
};

export const validateRequiredTrimmedText = ({
  value,
  label,
  isDisabled = false
}: {
  value?: string;
  label: string;
  isDisabled?: boolean;
}) => {
  if (isDisabled) return true;

  return validateAlphabetRule({
    value,
    label,
    isRequired: true
  });
};

export const validateLabelWhenNoContent = ({
  value,
  label,
  isDisabled = false,
  hasContent = false,
  isRequired = false
}: {
  value?: string;
  label: string;
  isDisabled?: boolean;
  hasContent?: boolean;
  isRequired?: boolean;
}) => {
  if (isDisabled) return true;

  if (hasContent && !value) return true;

  if (!isRequired && !value) return true;

  return validateAlphabetRule({
    value,
    label,
    isRequired
  });
};

export const VALIDATION_PAIR_CONFIG: Record<
  string,
  {
    pairedKey: string;
    comparison: 'lessThan' | 'greaterThan';
    message: string;
  }
> = {
  minLength: {
    pairedKey: 'maxLength',
    comparison: 'lessThan',
    message: 'Minimum length must be less than maximum length'
  },
  maxLength: {
    pairedKey: 'minLength',
    comparison: 'greaterThan',
    message: 'Maximum length must be greater than minimum length'
  },
  min: {
    pairedKey: 'max',
    comparison: 'lessThan',
    message: 'Min value must be less than max value'
  },
  max: {
    pairedKey: 'min',
    comparison: 'greaterThan',
    message: 'Max value must be greater than min value'
  },
  minCountOfKeywords: {
    pairedKey: 'maxCountOfKeywords',
    comparison: 'lessThan',
    message: 'Minimum keyword count must be less than maximum keyword count'
  },
  maxCountOfKeywords: {
    pairedKey: 'minCountOfKeywords',
    comparison: 'greaterThan',
    message: 'Maximum keyword count must be greater than minimum keyword count'
  },
  minItems: {
    pairedKey: 'maxItems',
    comparison: 'lessThan',
    message: 'Minimum item count must be less than maximum item count'
  },
  maxItems: {
    pairedKey: 'minItems',
    comparison: 'greaterThan',
    message: 'Maximum item count must be greater than minimum item count'
  }
};

export const DIGITS_ONLY_VALIDATION_KEYS = new Set([
  ...Object.keys(VALIDATION_PAIR_CONFIG),
  'maxSizeMB'
]);

const NON_NEGATIVE_KEYS = DIGITS_ONLY_VALIDATION_KEYS;
const MAX_FILE_SIZE_MB_LIMIT = 50;

export const getNumericValidationError = ({
  key,
  value,
  pairedValue
}: {
  key: string;
  value: string;
  pairedValue?: string;
}) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return true;

  if (DIGITS_ONLY_VALIDATION_KEYS.has(key) && !isDigitsOnly(trimmedValue)) {
    return 'Only numbers from 0-9 are allowed';
  }

  const currentNumber = toNumberIfFinite(trimmedValue);
  if (currentNumber === null) return 'Please enter a valid number';

  const isMinField = key.trim().toLowerCase().startsWith('min');
  const isMaxField = key.trim().toLowerCase().startsWith('max');
  if ((isMaxField || isMinField) && currentNumber < 1) {
    return toTitleCase(`${formatKeyPath(key)} must be at least 1`);
  }

  if (NON_NEGATIVE_KEYS.has(key) && currentNumber < 0) {
    return 'Value cannot be negative';
  }

  if (key === 'maxSizeMB' && currentNumber > MAX_FILE_SIZE_MB_LIMIT) {
    return `Max Size MB must not exceed ${MAX_FILE_SIZE_MB_LIMIT}`;
  }

  const pairConfig = VALIDATION_PAIR_CONFIG[key];
  if (!pairConfig) return true;

  const pairedTrimmedValue = pairedValue?.trim();
  if (!pairedTrimmedValue) return true;

  const pairedNumber = toNumberIfFinite(pairedTrimmedValue);
  if (pairedNumber === null) return true;

  if (
    (pairConfig.comparison === 'lessThan' && currentNumber >= pairedNumber) ||
    (pairConfig.comparison === 'greaterThan' && currentNumber <= pairedNumber)
  ) {
    return pairConfig.message;
  }

  return true;
};

export const getPairedValidationValue = (
  key: string,
  values?: Record<string, unknown>
): string => {
  const pairedKey = VALIDATION_PAIR_CONFIG[key]?.pairedKey;
  return pairedKey ? String(values?.[pairedKey] ?? '') : '';
};

export const toFormValues = (
  mode: 'add' | 'edit',
  sourceFieldOptions: SourceFieldOption[],
  defaultValues?: FieldEditorSubmit
): FieldFormValues => {
  if (mode === 'edit' && defaultValues) {
    const fallbackValues = toFieldFormValues(defaultValues);
    const currentField = defaultValues.field.trim().toLowerCase();

    const selectedSource = sourceFieldOptions.find(
      option => option.field.trim().toLowerCase() === currentField
    );

    return selectedSource?.dependentFieldFromReference
      ? {
          ...fallbackValues,
          dependentField: selectedSource.dependentFieldFromReference
        }
      : fallbackValues;
  }

  return EMPTY_FIELD_FORM;
};

export const buildFieldEditorSubmit = ({
  values,
  mode,
  selectedSourceField,
  defaultValues,
  mandatoryFieldNameSet
}: {
  values: FieldFormValues;
  mode: 'add' | 'edit';
  selectedSourceField?: SourceFieldOption;
  defaultValues?: FieldEditorSubmit;
  mandatoryFieldNameSet: Set<string>;
}) => {
  const baseSource = mode === 'add' ? selectedSourceField : undefined;
  const baseValidations =
    baseSource?.validations ?? defaultValues?.validations ?? {};
  const baseLabels = baseSource?.labels ?? defaultValues?.labels ?? {};

  const isMandatoryField = mandatoryFieldNameSet.has(
    normalizeFieldName(values.field)
  );

  const isRequiredLockedByMandatoryField =
    isMandatoryField &&
    normalizeFieldName(values.field) !== EXTRACTION_SKIP_CHECKBOX_FIELD_NAME;

  const nextValues = isRequiredLockedByMandatoryField
    ? { ...values, required: true }
    : values;

  return toFieldEditorSubmit({
    values: nextValues,
    baseValidations,
    baseLabels
  });
};
